"use client";

import { useChainSuggestions } from "@/hooks/useInsights";
import { getRecentChains, type RecentChain } from "@/lib/utils/recentSelections";
import { TOKEN_SERVICE_URL } from "@/consts/token-service";
import { normalizeX402Chains, orderChainsBySuggestion, type Chain, type X402Chain } from "@/lib/utils/chainSelection";
import * as React from "react";
interface UseChainSearchOptions {
  allowedChainIds?: number[];
  excludeChainIds?: number[];
  open: boolean;
  x402?: boolean;
}
interface UseChainSearchResult {
  chains: Chain[];
  handleSearchChange: (query: string) => void;
  isLoading: boolean;
  orderedChains: Chain[];
  recentChains: RecentChain[];
  refreshRecentChains: () => void;
  searchQuery: string;
  testnetFilter: boolean;
  setTestnetFilter: React.Dispatch<React.SetStateAction<boolean>>;
}
export function useChainSearch({
  allowedChainIds,
  excludeChainIds,
  open,
  x402 = false
}: UseChainSearchOptions): UseChainSearchResult {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [chains, setChains] = React.useState<Chain[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [recentChains, setRecentChains] = React.useState<RecentChain[]>([]);
  const [testnetFilter, setTestnetFilter] = React.useState(false);
  const [x402ChainsCache, setX402ChainsCache] = React.useState<Chain[] | null>(null);
  // Generation counter to discard stale API responses
  const searchGenRef = React.useRef(0);
  const suggestedChainIds = useChainSuggestions();
  const allowedSet = React.useMemo(() => allowedChainIds?.length ? new Set(allowedChainIds) : null, [allowedChainIds]);
  const excludeSet = React.useMemo(() => excludeChainIds?.length ? new Set(excludeChainIds) : null, [excludeChainIds]);
  const refreshRecentChains = React.useCallback(() => {
    const recents = getRecentChains();
    setRecentChains(allowedSet ? recents.filter(chain => allowedSet.has(chain.chainId)) : recents);
  }, [allowedSet]);
  const fetchX402Chains = React.useCallback(async (): Promise<X402Chain[]> => {
    try {
      const response = await fetch(`${TOKEN_SERVICE_URL}/chains?x402=true`);
      if (response.ok) {
        return response.json();
      }
    } catch (error) {
      console.error("Failed to fetch x402 chains:", error);
    }
    return [];
  }, []);
  React.useEffect(() => {
    if (!x402 || x402ChainsCache) {
      return;
    }
    fetchX402Chains().then(chains_0 => {
      setX402ChainsCache(normalizeX402Chains(chains_0));
    });
  }, [fetchX402Chains, x402, x402ChainsCache]);

  // Single effect that handles ALL chain fetching: open, search query changes, testnet toggle.
  // Debounces query changes (300ms) but fires immediately for open/testnet changes.
  React.useEffect(() => {
    if (!open) {
      setChains([]);
      setSearchQuery("");
      return;
    }
    const fetchChains = async (query: string) => {
      const gen = ++searchGenRef.current;
      setIsLoading(true);
      try {
        let results: Chain[];
        if (x402) {
          const cachedChains = x402ChainsCache || [];
          const lowerQuery = query.toLowerCase();
          results = lowerQuery ? cachedChains.filter(chain_0 => chain_0.name.toLowerCase().includes(lowerQuery)) : cachedChains;
        } else {
          const params = new URLSearchParams({
            limit: "50",
            testnet: testnetFilter.toString()
          });
          if (query) {
            params.set("query", query);
          }
          const response_0 = await fetch(`/api/chains?${params.toString()}`);
          if (response_0.ok) {
            const data = await response_0.json();
            results = data.results || [];
          } else {
            results = [];
          }
        }

        // Discard result if a newer search was started while we were fetching
        if (gen !== searchGenRef.current) return;
        let filtered = allowedSet ? results.filter(chain_1 => allowedSet.has(chain_1.chainId)) : results;
        if (excludeSet) {
          filtered = filtered.filter(chain_2 => !excludeSet.has(chain_2.chainId));
        }
        setChains(filtered);
      } catch (error_0) {
        if (gen !== searchGenRef.current) return;
        console.error("Failed to search chains:", error_0);
        setChains([]);
      } finally {
        if (gen === searchGenRef.current) {
          setIsLoading(false);
        }
      }
    };

    // Debounce search queries, but fetch immediately when there's no query
    // (initial open or clearing the search box)
    if (searchQuery) {
      const timeout = setTimeout(() => {
        void fetchChains(searchQuery);
      }, 300);
      return () => clearTimeout(timeout);
    }
    void fetchChains("");
  }, [open, searchQuery, testnetFilter, allowedSet, excludeSet, x402, x402ChainsCache]);
  const handleSearchChange = React.useCallback((query_0: string) => {
    setSearchQuery(query_0);
  }, []);
  React.useEffect(() => {
    refreshRecentChains();
  }, [refreshRecentChains]);
  const orderedChains = React.useMemo(() => orderChainsBySuggestion(chains, suggestedChainIds, searchQuery), [chains, searchQuery, suggestedChainIds]);
  return {
    chains,
    handleSearchChange,
    isLoading,
    orderedChains,
    recentChains,
    refreshRecentChains,
    searchQuery,
    testnetFilter,
    setTestnetFilter
  };
}